import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import styled from 'styled-components';

import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';

import { BaseTab, baseTabStyles, TabActiveProps, TabContent } from './common';
import { Header, HeaderProps } from './header';

export type RouterTab = BaseTab & {
  section: string;
};

export type RouterTabsHeaderProps = Omit<HeaderProps, 'tabs'> & {
  tabs: RouterTab[];
  hideTabs?: boolean;
  isLoading?: boolean;
};

export const RouterTabsHeader = ({ tabs, isLoading, ...rest }: RouterTabsHeaderProps) => {
  return (
    <>
      <Header tabs={<Tabs tabs={tabs} />} {...rest} />
      <Routes>
        {tabs.map((tab) => (
          <Route
            key={tab.section}
            path={tab.section}
            element={isLoading ? <Spinner /> : tab.content}
          />
        ))}
        <Route path="*" element={<Navigate to={tabs[0].section} replace />} />
      </Routes>
    </>
  );
};

type TabsProps = {
  tabs: RouterTab[];
};

const Tabs = ({ tabs }: TabsProps) => {
  const location = useLocation();
  const selectedSection = location.pathname.split('/').pop();

  return (
    <Stack orientation="horizontal">
      {tabs.map((tab) => (
        <SingleTab tab={tab} key={tab.section} selectedSection={selectedSection} />
      ))}
    </Stack>
  );
};

type SingleTabProps = {
  tab: RouterTab;
  selectedSection?: string;
};

const SingleTab = ({ tab, selectedSection }: SingleTabProps) => {
  const isActive = tab.section === selectedSection;

  return (
    <Tab to={tab.section} data-testid={tab.dataTestId} $active={isActive}>
      <TabContent tab={tab} isActive={isActive} />
    </Tab>
  );
};

const Tab = styled(Link)<TabActiveProps>(baseTabStyles);
